
import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Modal,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetPassword } from "../../redux/usersSlice";
import Captcha from "./Captcha";
import backgroundImage from "../../assets/icons/twoFactorBackground.png";

// 🎨 Design tokens
const NAVY = "#2F4E8B";
const NAVY_LIGHT = "#2F4E8B";
const OUTLINE = "#D4D9E0";

// Underline-only TextField
const CustomTextField = styled("input")(() => ({
  fontSize: 16,
  padding: "8px 0",
  border: "none",
  borderBottom: `1px solid ${OUTLINE}`,
  outline: "none",
  width: "100%",
  "&:focus": {
    borderBottom: `2px solid ${NAVY}`,
  },
}));

const ResetPasswordForm = () => {
  const [nouveauMdp, setNouveauMdp] = useState("");
  const [confirmationMdp, setConfirmationMdp] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validatePasswords = () => {
    if (nouveauMdp.length < 8 || !/\d/.test(nouveauMdp)) {
      setFieldError(
        "Le mot de passe doit contenir ≥ 8 caractères et au moins un chiffre."
      );
      return false;
    }
    if (nouveauMdp !== confirmationMdp) {
      setFieldError("Les mots de passe ne correspondent pas.");
      return false;
    }
    setFieldError("");
    return true;
  };

  const handleCaptchaSuccess = (captchaVerification) => {
    setCaptchaOpen(false);
    if (!validatePasswords()) return;

    setLoading(true);
    setSubmitError("");
    const token = new URLSearchParams(window.location.search).get("token");

    dispatch(
      resetPassword({
        token,
        newPassword: nouveauMdp,
        confirmNewPassword: confirmationMdp,
        captchaVerification,
      })
    )
      .unwrap()
      .then(() => {
        setSuccess("Mot de passe réinitialisé avec succès.");
        setTimeout(() => navigate("/connexion"), 1500);
      })
      .catch(() => {
        setSubmitError("Token invalide ou expiré.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          position: "relative",
          borderRadius: 4,
          width: "100%",
          maxWidth: 780,
          p: { xs: 5, sm: 6 },
          bgcolor: "#fff",
        }}
      >
        {/* Titre + sous-texte */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: NAVY, mb: 1 }}>
            Réinitialiser le mot de passe
          </Typography>
          <Typography variant="body2" sx={{ color: "#555" }}>
            Veuillez saisir votre nouveau mot de passe ci-dessous, puis
            confirmer.
          </Typography>
        </Box>

        {/* Séparateur */}
        <Divider sx={{ mb: 4 }} />

        {/* Formulaire centré */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            alignItems: "center",
          }}
        >
          {/* Nouveau mot de passe */}
          <Box sx={{ width: 360 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Nouveau mot de passe
            </Typography>
            <CustomTextField
              type="password"
              value={nouveauMdp}
              onChange={(e) => setNouveauMdp(e.target.value)}
            />
            {fieldError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                ⚠️ {fieldError}
              </Typography>
            )}
          </Box>

          {/* Confirmation */}
          <Box sx={{ width: 360 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Confirmer le mot de passe
            </Typography>
            <CustomTextField
              type="password"
              value={confirmationMdp}
              onChange={(e) => setConfirmationMdp(e.target.value)}
            />
          </Box>

          {/* Bouton */}
          <Button
            variant="contained"
            sx={{
              width: 200,
              height: 40,
              borderRadius: 30,
              textTransform: "none",
              fontWeight: 600,
              bgcolor: NAVY,
              "&:hover": { bgcolor: NAVY_LIGHT },
            }}
            onClick={() => {
              setFieldError("");
              setSubmitError("");
              setSuccess("");
              setCaptchaOpen(true);
            }}
            disabled={loading}
          >
            {loading ? "Traitement..." : "Confirmer"}
          </Button>

          {/* Messages */}
          {submitError && <Alert severity="error">{submitError}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Box>

        <Typography
          variant="body2"
          align="center"
          sx={{ color: "#999999", mt: 6 }}
        >
          © 2025 Tallentum. Tous droits réservés.
        </Typography>
      </Paper>

      {/* Modal Captcha */}
      <Modal
        open={captchaOpen}
        onClose={(e, reason) => {
          if (["backdropClick", "escapeKeyDown"].includes(reason)) return;
          setCaptchaOpen(false);
        }}
        disableEscapeKeyDown
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 600, md: 800 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            border: `2px solid ${NAVY}`,
          }}
        >
          <Captcha
            onSuccess={handleCaptchaSuccess}
            handleClose={() => setCaptchaOpen(false)}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default ResetPasswordForm;
